import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import {
  fetchInviteByToken,
  stashPendingLinkToken,
} from '../../services/inviteService'
import type { PendingInvite } from '../../types/invites'
import { Button } from '../Common'
import { NotFoundPage } from '../NotFoundPage'
import { useNls } from '../../nls/nlsProvider'

type LandingState =
  | { status: 'LOADING' }
  | { status: 'INVALID' }
  | { status: 'READY'; invite: PendingInvite }

const formatExpiry = (expiresAt: number | null | undefined): string => {
  if (!expiresAt) return 'soon'
  try {
    return new Date(expiresAt).toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
    })
  } catch {
    return 'soon'
  }
}

export const HelperInviteLanding = ({ token }: { token: string }) => {
  const { t } = useNls()
  const [state, setState] = useState<LandingState>({ status: 'LOADING' })

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const invite = await fetchInviteByToken(token)
        if (cancelled) return
        // Any non-PENDING / non-existent / expired invite collapses to the
        // same "URL doesn't exist" surface — we never confirm to a URL
        // guesser that /invite/{token} is a real route; genuine invitees who
        // hit a revoked/claimed link learn about it from their admin.
        if (
          !invite
          || invite.status === 'REVOKED'
          || invite.status === 'CLAIMED'
          || (typeof invite.expiresAt === 'number' && invite.expiresAt > 0 && invite.expiresAt < Date.now())
        ) {
          setState({ status: 'INVALID' })
          return
        }
        setState({ status: 'READY', invite })
      } catch {
        if (!cancelled) setState({ status: 'INVALID' })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [token])

  const handleContinue = () => {
    if (state.status !== 'READY') return
    stashPendingLinkToken(state.invite.token)
    if (typeof window !== 'undefined') {
      window.history.pushState({}, '', '/helper')
      window.dispatchEvent(new PopStateEvent('popstate'))
    }
  }

  if (state.status === 'INVALID') {
    return <NotFoundPage path={typeof window !== 'undefined' ? window.location.pathname : `/invite/${token}`} />
  }

  return (
    <main className="min-h-screen flex flex-col items-center justify-center p-4 bg-[var(--bg)] text-[var(--text-strong)]">
      <motion.section
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 120, damping: 22 }}
        className="w-full max-w-sm rounded-3xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-6 shadow-[var(--shadow-card)] flex flex-col gap-4"
        data-testid="helper-invite-landing"
      >
        <header>
          <p className="text-xs uppercase tracking-widest text-[var(--muted)]">{t('NLSHelperInviteEyebrow')}</p>
          <h1 className="text-2xl font-semibold text-[var(--text-strong)] mt-1">
            {t('NLSHelperInviteTitle')}
          </h1>
        </header>

        {state.status === 'LOADING' && (
          <p className="text-sm text-[var(--muted)]">{t('NLSHelperInviteValidating')}</p>
        )}

        {state.status === 'READY' && (
          <>
            <div className="rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] p-3 text-sm text-[var(--text-strong)] flex flex-col gap-1">
              {state.invite.invitedByName && (
                <p>{t('NLSHelperInviteFrom', { name: state.invite.invitedByName })}</p>
              )}
              <p className="text-xs text-[var(--muted)]">
                {t('NLSHelperInviteJoining', { name: state.invite.workspaceName })}
              </p>
              {state.invite.expiresAt ? (
                <p className="text-xs text-[var(--muted)]">
                  {t('NLSHelperInviteExpires', { expiry: formatExpiry(state.invite.expiresAt) })}
                </p>
              ) : null}
            </div>
            <Button
              variant="primary"
              size="lg"
              onClick={handleContinue}
            >
              {t('NLSHelperInviteContinue')}
            </Button>
            <p className="text-[11px] text-[var(--muted)]">
              {t('NLSHelperInviteFooter')}
            </p>
          </>
        )}
      </motion.section>
    </main>
  )
}
